import { useRef, useState, type KeyboardEvent } from 'react';
import { useTranslation } from 'react-i18next';
import { FinanceOverviewTab } from '@/pages/finance/FinanceOverviewTab';
import { FinanceExpensesTab } from '@/pages/finance/FinanceExpensesTab';
import './FinancePage.css';

type TabKey = 'overview' | 'expenses';

interface TabDef {
  key: TabKey;
  labelKey: string;
}

const TABS: TabDef[] = [
  { key: 'overview', labelKey: 'finance.tabs.overview' },
  { key: 'expenses', labelKey: 'finance.tabs.expenses' },
];

/**
 * `/finance` (T127, 17-finance-ux.md §3) — dedicated Finance module (Admin + Accounts Officer,
 * DEC-FIN6 FINAL), gated by `RoleGuard` in `router.tsx`. One route, two ARIA tabs — exactly the
 * `FinanceSettingsPage.tsx`/`LookupsPage.tsx` tabs pattern reused verbatim (roving `tabIndex`,
 * arrow-key nav flipped per `dir`, `Home`/`End`, only the active panel mounted). Both tabs are
 * visible to the exact same role set — the split organizes content density, not RBAC (§3.1).
 */
export function FinancePage() {
  const { t, i18n } = useTranslation();
  const [activeIndex, setActiveIndex] = useState(0);
  const tabRefs = useRef<Array<HTMLButtonElement | null>>([]);
  const isRtl = i18n.language.startsWith('ar');

  function focusTab(index: number) {
    const normalized = (index + TABS.length) % TABS.length;
    setActiveIndex(normalized);
    tabRefs.current[normalized]?.focus();
  }

  function handleKeyDown(e: KeyboardEvent<HTMLButtonElement>, index: number) {
    switch (e.key) {
      case 'ArrowRight':
        e.preventDefault();
        focusTab(index + (isRtl ? -1 : 1));
        break;
      case 'ArrowLeft':
        e.preventDefault();
        focusTab(index + (isRtl ? 1 : -1));
        break;
      case 'Home':
        e.preventDefault();
        focusTab(0);
        break;
      case 'End':
        e.preventDefault();
        focusTab(TABS.length - 1);
        break;
      default:
        break;
    }
  }

  const activeTab = TABS[activeIndex];

  return (
    <section className="fa-finance-page">
      <div className="fa-finance-page__header">
        <h1 className="fa-finance-page__title">{t('finance.pageTitle')}</h1>
        <p className="fa-finance-page__description">{t('finance.pageSubtitle')}</p>
      </div>

      <div
        role="tablist"
        aria-label={t('finance.tabsAriaLabel')}
        className="fa-finance-page__tablist"
      >
        {TABS.map((tab, index) => (
          <button
            key={tab.key}
            ref={(el) => {
              tabRefs.current[index] = el;
            }}
            type="button"
            role="tab"
            id={`finance-page-tab-${tab.key}`}
            aria-selected={activeIndex === index}
            aria-controls={`finance-page-tabpanel-${tab.key}`}
            tabIndex={activeIndex === index ? 0 : -1}
            className={`fa-finance-page__tab ${
              activeIndex === index ? 'fa-finance-page__tab--active' : ''
            }`}
            onClick={() => setActiveIndex(index)}
            onKeyDown={(e) => handleKeyDown(e, index)}
          >
            {t(tab.labelKey)}
          </button>
        ))}
      </div>

      <div
        role="tabpanel"
        id={`finance-page-tabpanel-${activeTab.key}`}
        aria-labelledby={`finance-page-tab-${activeTab.key}`}
        tabIndex={0}
        className="fa-finance-page__tabpanel"
      >
        {activeTab.key === 'overview' ? <FinanceOverviewTab /> : <FinanceExpensesTab />}
      </div>
    </section>
  );
}
